import fetchGetRedRestApi from "@scripts/services/fetch-get-red-rest-api";
import { getStore } from "@scripts-admin/utils/safe-store";

export default (initial = {}) => ({
    storeName: initial.store ?? null,
    singleChoice: initial.singleChoice ?? false,
    roots: [],
    divisions: [],
    loading: false,
    error: null,
    withChild: false,
    selectedIds: [],
    select2: null,

    _byId: new Map(),
    _childrenById: new Map(),
    _depthById: new Map(),

    init() {
        this.$st = getStore(this.storeName ?? '') || {};
        this._applyStoreState();
        this.load();

        this.$watch('withChild', (val) => {
            if (val) {
                this._enforceMutualExclusion();
            }

            this.$st.divisionWithChild = val;
        });

        this.$watch('selectedIds', (ids) => {
            if (this.select2) {
                this.select2.val(ids).trigger('change.select2');
            }
        });

        this.$watch(() => this.$st?.divisionWithChild, (val) => {
            if (typeof val === 'undefined') return;
            const boolVal = Boolean(val);
            if (boolVal === this.withChild) return;
            this.withChild = boolVal;
        });

        this.$watch(() => this.$st?.selectedDivisions, (ids) => {
            if (!Array.isArray(ids)) ids = [];
            const normalized = ids.map(id => Number(id));
            if (this._areIdsEqual(normalized, this.selectedIds)) {
                return;
            }
            this.selectedIds = normalized;
        });
    },

    async load() {
        this.loading = true; this.error = null;
        try {
            const { success, divisionsTree, divisions } = await fetchGetRedRestApi('all-divisions');
            if (success) {
                this.roots = divisionsTree;
                this.allDivisions = divisions;
                this.divisions = divisions.map(div => ({
                    id: div.id,
                    text: div.name
                }));
                this._buildIndexes();
                this.initMultiSelect();

                if (this.withChild && this.selectedIds.length) {
                    this._enforceMutualExclusion();
                }
            }
        } catch (e) {
            this.error = e.message || 'Load error';
        } finally {
            this.loading = false;
        }
    },

    // ---------- SELECT ----------
    select(node) {
        const isSelected = this.selectedIds.includes(node.id);

        if (isSelected) {
            // зняти тільки поточний
            this.selectedIds = this.selectedIds.filter(id => id !== node.id);
            this.$st.selectedDivisions = (this.$st.selectedDivisions || []).filter(id => id !== node.id);
            if (this.$st.selectedDivisionPaths) delete this.$st.selectedDivisionPaths[node.id];
            return;
        }

        // додати поточний
        if (!this.singleChoice) {
            this.selectedIds = [...this.selectedIds, node.id];
            this.$st.selectedDivisions = [...(this.$st.selectedDivisions || []), node.id];
        } else {
            this.selectedIds = [node.id];
            this.$st.selectedDivisions = this.selectedIds;
        }

        // зберегти шлях
        const pathNames = this._getPathNames(node.id);
        if (!this.singleChoice) {
            this.$st.selectedDivisionPaths = this.$st.selectedDivisionPaths || {};
        } else {
            this.$st.selectedDivisionPaths = {};
        }
        this.$st.selectedDivisionPaths[node.id] = pathNames.join(' -> ');

        // якщо взаємовиключення — наведи порядок після додавання
        if (this.withChild) {
            this._enforceMutualExclusion(node.id);
        }

        this.openParentNodes(node.id);
    },

    openParentNodes(id) {
        const currentNode = this.allDivisions.find(node => Number(node?.id) === Number(id));
        
        if (currentNode?.parent_id === 0) {
            return;
        }

        const parentNode = this.findNodeById(this.roots, currentNode?.parent_id);

        if (parentNode) {
            parentNode._open = true;

            this.openParentNodes(parentNode?.id);
        }
    },
    
    _applyStoreState() {
        if (Array.isArray(this.$st?.selectedDivisions)) {
            this.selectedIds = this.$st.selectedDivisions.map(id => Number(id));
        }

        if (typeof this.$st?.divisionWithChild !== 'undefined') {
            this.withChild = Boolean(this.$st.divisionWithChild);
        }
    },

    findNodeById(roots, targetId) {
        for (const root of roots) {
            const found = this.dfs(root, targetId);
            if (found) return found;
        }
        return null;
    },

    _areIdsEqual(arrA = [], arrB = []) {
        if (arrA.length !== arrB.length) return false;
        const sortA = [...arrA].map(Number).sort((a, b) => a - b);
        const sortB = [...arrB].map(Number).sort((a, b) => a - b);
        return sortA.every((val, idx) => val === sortB[idx]);
    },

    dfs(node, id) {
        if (Number(node.id) === Number(id)) return node;

        const kids = node.children || [];
        for (const child of kids) {
            const res = this.dfs(child, id);
            if (res) return res;
        }
        return null;
    },

    // ---------- HELPERS ----------
    _buildIndexes() {
        this._byId.clear();
        this._childrenById.clear();
        this._depthById.clear();

        const walk = (nodes, parentId = 0, depth = 0) => {
            for (const n of nodes) {
                this._byId.set(n.id, n);
                this._depthById.set(n.id, depth);
                if (!this._childrenById.has(n.id)) this._childrenById.set(n.id, []);
                if (parentId) {
                    if (!this._childrenById.has(parentId)) this._childrenById.set(parentId, []);
                    this._childrenById.get(parentId).push(n.id);
                }
                if (n.children?.length) walk(n.children, n.id, depth + 1);
            }
        };
        walk(this.roots);
    },

    _getPathNames(id) {
        const names = [];
        let cur = this._byId.get(id);
        while (cur) {
            names.push(cur.name);
            if (!cur.parent_id || cur.parent_id === 0) break;
            cur = this._byId.get(cur.parent_id);
        }
        return names.reverse();
    },

    _collectDescendantIds(rootId) {
        const res = [];
        const stack = [rootId];
        while (stack.length) {
            const id = stack.pop();
            const kids = this._childrenById.get(id) || [];
            for (const k of kids) {
                res.push(k);
                stack.push(k);
            }
        }
        return res;
    },

    _collectAncestorIds(id) {
        const res = [];
        let cur = this._byId.get(id);
        while (cur && cur.parent_id && cur.parent_id !== 0) {
            res.push(cur.parent_id);
            cur = this._byId.get(cur.parent_id);
        }
        return res; // від найближчого предка до кореня
    },

    // --- ГОЛОВНЕ: взаємовиключення предок ↔ нащадок ---
    _enforceMutualExclusion(lastAddedId = null) {
        if (!this.selectedIds.length) return;

        // 1) Спершу сформуємо порядок обходу:
        //    - якщо є lastAddedId — обробляємо його ПЕРШИМ
        //    - решту — за спаданням глибини (найглибші спочатку)
        const rest = this.selectedIds.filter(id => id !== lastAddedId);
        const restByDepthDesc = rest.sort((a, b) => {
            const da = this._depthById.get(a) ?? 0;
            const db = this._depthById.get(b) ?? 0;
            return db - da;
        });

        const order = lastAddedId != null ? [lastAddedId, ...restByDepthDesc] : restByDepthDesc;

        const keep = new Set();
        const ban = new Set();

        for (const id of order) {
            if (ban.has(id)) continue;

            // фіксуємо цей id і банимо всіх його предків і нащадків
            keep.add(id);

            const ancestors = this._collectAncestorIds(id);
            const descendants = this._collectDescendantIds(id);
            for (const x of ancestors) ban.add(x);
            for (const x of descendants) ban.add(x);
        }

        // 2) Синхронізація стану
        const nextSelected = [...keep];

        this.selectedIds = nextSelected;
        this.$st.selectedDivisions = nextSelected;

        if (this.$st.selectedDivisionPaths) {
            for (const key of Object.keys(this.$st.selectedDivisionPaths)) {
                const id = Number(key);
                if (!keep.has(id)) delete this.$st.selectedDivisionPaths[id];
            }
        } else {
            this.$st.selectedDivisionPaths = {};
        }

        for (const id of keep) {
            if (!this.$st.selectedDivisionPaths[id]) {
                const p = this._getPathNames(id);
                this.$st.selectedDivisionPaths[id] = p.join(' -> ');
            }
        }
    },

    getPathString(id, sep = ' -> ') {
        return this._getPathNames(id).join(sep);
    },

    initMultiSelect() {
        this.select2 = jQuery(this.$refs.multiselect).select2({
            data: this.divisions,
            placeholder: 'Почніть вводити…',
            minimumInputLength: 2,
            matcher: this.smartMatcher,
            width: 'resolve',
        });

        this._syncing = false;

        this.select2.on('select2:select', (e) => {
            if (this._syncing) return;

            const { id } = e.params.data;
            this.triggerClickByDivisionId(id);
        });

        this.select2.on('select2:unselect', (e) => {
            if (this._syncing) return;

            const { id } = e.params.data;
            this.triggerClickByDivisionId(id);
        });
    },

    smartMatcher(params, data) {
        const normalize = (s) => s
            .toString()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

        if (!params.term || !data.text) return data;
        const term = normalize(params.term);
        const hay = normalize(data.text);
        return hay.indexOf(term) > -1 ? data : null;
    },

    triggerClickByDivisionId(id) {
        const cssEscape = window.CSS && CSS.escape
            ? CSS.escape
            : (s) => String(s).replace(/["\\#.;:[\]>*+~^$|=(),\s]/g, '\\$&');

        const el =
            this.$root.querySelector(`[data-id="${cssEscape(id)}"] a`) ||
            this.$root.querySelector(`[data-id="${cssEscape(id)}"]`);

        if (el) {
            this._syncing = true;
            try {
                el.click();
            } finally {
                requestAnimationFrame(() => { this._syncing = false; });
            }
        }
    },
});
